import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import {
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  Info,
  Pencil,
  Plus,
  Sparkles,
  Star,
  StarOff,
  Trash2,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  useAdminNichesWithUsage,
  useCreateNiche,
  useUpdateNiche,
  useDeleteNiche,
  useAdminCategories,
  useAdminRates,
  useCreateRate,
  useUpdateRate,
  useDeactivateRate,
  type OutreachCategory,
} from '@/hooks/useAdmin';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Pill } from '@/components/ui/pill';
import { Tag } from '@/components/ui/tag';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  DataTable,
  DataTableExpandedCell,
  DataTableHeader,
  DataTableHeaderCell,
  DataTableRow,
} from '@/components/ui/data-table';
import { PageHeader } from '@/components/ui/page-header';
import { Segmented, SortHeader, type SortState } from '@/pages/admin/insights/parts';
import { formatMoney } from '@/lib/money';
import { formatDate } from '@/lib/dates';
import type { NicheUsage, OutreachNicheWithUsage, OutreachRate } from '@/api/types';
import { creatorTypeLabel, rateTypeLabel } from '@/lib/labels';

const FOCUS_EXPLAINER =
  "Focus niches are starred for outreachers on their dashboard, rates page and niche picker, and steer discovery's search ideas toward them. Focus doesn't change what an invite earns: that's the niche's bonuses below.";

/**
 * Invites are re-priced every morning at current rates until SPA-1207 ships,
 * so any bonus change reaches unpaid invites at the next run. Say so wherever
 * a bonus is added, changed or removed.
 */
function repriceNote(nicheName: string, outcome = "they'll earn the new amount"): string {
  return `Unpaid invites whose first niche is ${nicheName} are re-priced at the next morning run (09:00 UTC), so ${outcome}. Amounts already paid don't change.`;
}

/** The components the payout calculator reads for a niche bonus, in pay order. */
const BONUS_COMPONENTS = ['LOG_BONUS', 'JOIN_BONUS', 'POST_BASE'] as const;
type BonusComponent = (typeof BONUS_COMPONENTS)[number];
const isBonusComponent = (t: string): t is BonusComponent =>
  (BONUS_COMPONENTS as readonly string[]).includes(t);

type SortKey = 'focus' | 'name' | 'bonus' | 'invites' | 'lastUsed';
const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'focus', label: 'Focus' },
  { value: 'name', label: 'Name' },
  { value: 'bonus', label: 'Bonus' },
  { value: 'invites', label: 'Invites' },
  { value: 'lastUsed', label: 'Last used' },
];
/** Name reads A→Z first; everything else biggest / newest / focused first. */
const defaultDir = (key: SortKey): SortState<SortKey>['dir'] => (key === 'name' ? 'asc' : 'desc');

/** What still points at a niche, in the backend's wording. Empty → removable. */
function nicheReferences(u: NicheUsage): string[] {
  const out: string[] = [];
  const add = (n: number, one: string, many: string): void => {
    if (n > 0) out.push(`${n.toLocaleString()} ${n === 1 ? one : many}`);
  };
  add(u.invitesTotal, 'invite', 'invites');
  add(u.prospects, 'prospect', 'prospects');
  add(u.creatorRequests, 'creator request', 'creator requests');
  add(u.challenges, 'challenge', 'challenges');
  add(u.ratesActive, 'active niche rate', 'active niche rates');
  add(u.ratesInactive, 'turned-off niche rate', 'turned-off niche rates');
  add(u.discoverySuggestions, 'discovery search suggestion', 'discovery search suggestions');
  return out;
}

interface CreateValues {
  code: string;
  displayName: string;
  isFocused: boolean;
  categoryCodes: string[];
}

interface EditValues {
  displayName: string;
  categoryCodes: string[];
}

const EMPTY_CREATE: CreateValues = {
  code: '',
  displayName: '',
  isFocused: false,
  categoryCodes: [],
};

export function AdminNiches(): JSX.Element {
  const niches = useAdminNichesWithUsage();
  const categories = useAdminCategories();
  const rates = useAdminRates();
  const create = useCreateNiche();
  const update = useUpdateNiche();
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<OutreachNicheWithUsage | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [showHidden, setShowHidden] = useState(false);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortState<SortKey>>({ key: 'focus', dir: 'desc' });
  const createForm = useForm<CreateValues>({ defaultValues: EMPTY_CREATE });
  const editForm = useForm<EditValues>({ defaultValues: { displayName: '', categoryCodes: [] } });

  const activeCategories = (categories.data ?? []).filter((c) => c.isActive);
  const categoryByCode = useMemo(
    () => new Map((categories.data ?? []).map((c) => [c.code, c])),
    [categories.data],
  );
  const categoryName = (code: string): string =>
    categoryByCode.get(code)?.displayName ?? creatorTypeLabel(code);

  // Map each niche code → active rates targeting it.
  const ratesByNiche = useMemo(() => {
    const out = new Map<string, OutreachRate[]>();
    for (const r of rates.data ?? []) {
      if (!r.isActive || r.niche === null) continue;
      const list = out.get(r.niche) ?? [];
      list.push(r);
      out.set(r.niche, list);
    }
    return out;
  }, [rates.data]);

  // The most one invite can earn from this niche's bonuses: per creator
  // type, logged + joined + one approved post. Summing across creator types
  // would add amounts no single invite can earn. Only the components the
  // payout calculator reads count, and not for flat-rate creator types.
  const bonusOf = useMemo(() => {
    const out = new Map<string, number>();
    for (const [code, list] of ratesByNiche) {
      const perType = new Map<string, number>();
      for (const r of list) {
        if (!isBonusComponent(r.rateType)) continue;
        if (categoryByCode.get(r.category)?.payoutFormula === 'SIMPLE_LOG_BONUS') continue;
        const v = Number.parseFloat(r.amount);
        perType.set(r.category, (perType.get(r.category) ?? 0) + (Number.isFinite(v) ? v : 0));
      }
      out.set(code, Math.max(0, ...perType.values()));
    }
    return out;
  }, [ratesByNiche, categoryByCode]);

  const all = niches.data ?? [];
  const hiddenCount = all.filter((n) => n.isHidden).length;
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = all.filter(
      (n) =>
        (showHidden || !n.isHidden) &&
        (!q || n.displayName.toLowerCase().includes(q) || n.code.toLowerCase().includes(q)),
    );
    const primary = (a: OutreachNicheWithUsage, b: OutreachNicheWithUsage): number => {
      switch (sort.key) {
        case 'focus':
          return Number(a.isFocused) - Number(b.isFocused);
        case 'name':
          return a.displayName.localeCompare(b.displayName);
        case 'bonus':
          return (bonusOf.get(a.code) ?? 0) - (bonusOf.get(b.code) ?? 0);
        case 'invites':
          return a.usage.invitesLive - b.usage.invitesLive;
        case 'lastUsed':
          return (a.usage.lastUsedAt ?? '').localeCompare(b.usage.lastUsedAt ?? '');
      }
    };
    return rows.sort((a, b) => {
      const p = primary(a, b);
      if (p !== 0) return sort.dir === 'desc' ? -p : p;
      return a.displayName.localeCompare(b.displayName);
    });
  }, [all, showHidden, search, sort, bonusOf]);

  function sortBy(key: SortKey): void {
    setSort((s) =>
      s.key === key ? { key, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: defaultDir(key) },
    );
  }

  function toggleCreateCategory(code: string): void {
    const current = createForm.getValues('categoryCodes');
    const next = current.includes(code) ? current.filter((c) => c !== code) : [...current, code];
    createForm.setValue('categoryCodes', next, { shouldDirty: true });
  }
  function toggleEditCategory(code: string): void {
    const current = editForm.getValues('categoryCodes');
    const next = current.includes(code) ? current.filter((c) => c !== code) : [...current, code];
    editForm.setValue('categoryCodes', next, { shouldDirty: true });
  }
  const watchCreateCats = createForm.watch('categoryCodes');
  const watchEditCats = editForm.watch('categoryCodes');

  async function onCreate(values: CreateValues): Promise<void> {
    try {
      await create.mutateAsync(values);
      toast.success('Niche added');
      createForm.reset(EMPTY_CREATE);
      setShowCreate(false);
    } catch (err) {
      toast.error(`Couldn't add the niche: ${(err as Error).message}`);
    }
  }

  function openEdit(niche: OutreachNicheWithUsage): void {
    editForm.reset({ displayName: niche.displayName, categoryCodes: niche.categoryCodes });
    setEditing(niche);
  }

  async function onEdit(values: EditValues): Promise<void> {
    if (!editing) return;
    try {
      await update.mutateAsync({
        id: editing.id,
        body: {
          displayName: values.displayName,
          categoryCodes: values.categoryCodes,
        },
      });
      toast.success('Niche saved');
      setEditing(null);
    } catch (err) {
      toast.error(`Couldn't save the niche: ${(err as Error).message}`);
    }
  }

  const colCount = 8;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Niches"
        description={
          <>
            Content topics creators post about. Outreachers tag each invite with one or more niches;
            a niche&apos;s bonuses are added on top of the base rate when it&apos;s the invite&apos;s
            first niche. Open a niche to change its bonuses, hide it or remove it.
          </>
        }
        actions={
          <Button
            onClick={() => {
              createForm.reset(EMPTY_CREATE);
              setShowCreate(true);
            }}
          >
            <Plus className="h-4 w-4" /> Add niche
          </Button>
        }
      />

      {showCreate && (
        <Card>
          <CardHeader>
            <CardTitle>New niche</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={createForm.handleSubmit(onCreate)} className="grid gap-3" noValidate>
              <div className="grid gap-1">
                <Label htmlFor="code">ID</Label>
                <Input
                  id="code"
                  placeholder="gaming"
                  {...createForm.register('code', { required: true })}
                />
                <p className="text-xs text-muted-foreground">
                  A short lowercase ID. It can&apos;t be changed later.
                </p>
              </div>
              <div className="grid gap-1">
                <Label htmlFor="displayName">Name</Label>
                <Input
                  id="displayName"
                  placeholder="Gaming"
                  {...createForm.register('displayName', { required: true })}
                />
              </div>
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" className="mt-0.5" {...createForm.register('isFocused')} />
                <span>
                  Make it a focus niche
                  <span className="block text-xs text-muted-foreground">{FOCUS_EXPLAINER}</span>
                </span>
              </label>
              <CategoryChips
                categories={activeCategories}
                selected={watchCreateCats}
                onToggle={toggleCreateCategory}
              />
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={create.isPending}>
                  Create
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {editing && (
        <Card>
          <CardHeader>
            <CardTitle>Rename "{editing.displayName}"</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={editForm.handleSubmit(onEdit)} className="grid gap-3" noValidate>
              <div className="grid gap-1">
                <Label htmlFor="editDisplayName">Name</Label>
                <Input
                  id="editDisplayName"
                  {...editForm.register('displayName', { required: true, minLength: 1 })}
                />
                <p className="text-xs text-muted-foreground">
                  Its ID ({editing.code}) stays the same, so past invites keep this niche. Only the
                  name and creator types change.
                </p>
              </div>
              <CategoryChips
                categories={activeCategories}
                selected={watchEditCats}
                onToggle={toggleEditCategory}
              />
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={update.isPending}>
                  Save
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Find a niche…"
          aria-label="Find a niche"
          className="w-full sm:w-56"
        />
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Sort</span>
          <Segmented label="Sort niches by" value={sort.key} options={SORT_OPTIONS} onChange={sortBy} />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={showHidden}
            onChange={(e) => setShowHidden(e.target.checked)}
          />
          Show hidden ({hiddenCount})
        </label>
      </div>

      <DataTable aria-label="Niches">
        <DataTableHeader>
          <tr>
            <DataTableHeaderCell narrow className="hidden sm:table-cell" />
            <SortHeader label="Niche" k="name" sort={sort} onSort={sortBy} numeric={false} />
            <DataTableHeaderCell>Creator types</DataTableHeaderCell>
            <SortHeader label="Focus" k="focus" sort={sort} onSort={sortBy} numeric={false} />
            <SortHeader
              label="Niche bonus"
              k="bonus"
              sort={sort}
              onSort={sortBy}
              def="The most one invite can earn from this niche's bonuses, for its best-paying creator type: when logged + when joined + one approved post."
            />
            <SortHeader
              label="Invites"
              k="invites"
              sort={sort}
              onSort={sortBy}
              def="Invites tagged with this niche (any position) that aren't rejected or hidden."
            />
            <SortHeader label="Last used" k="lastUsed" sort={sort} onSort={sortBy} />
            <DataTableHeaderCell />
          </tr>
        </DataTableHeader>
        <tbody>
          {niches.isLoading && (
            <tr>
              <td colSpan={colCount} className="px-3 py-6 text-center text-sm text-muted-foreground">
                Loading niches…
              </td>
            </tr>
          )}
          {!niches.isLoading && visible.length === 0 && (
            <tr>
              <td colSpan={colCount} className="px-3 py-6 text-center text-sm text-muted-foreground">
                {search ? `No niches match "${search}".` : 'No niches yet.'}
              </td>
            </tr>
          )}
          {visible.map((n) => {
            const linkedRates = ratesByNiche.get(n.code) ?? [];
            const totalBonus = bonusOf.get(n.code) ?? 0;
            const isExpanded = expandedId === n.id;
            return (
              <Fragment key={n.id}>
                <DataTableRow
                  state={isExpanded ? 'expanded' : n.isHidden ? 'terminal' : 'default'}
                >
                  <td className="hidden w-6 px-3 py-2 align-middle sm:table-cell">
                    <button
                      type="button"
                      onClick={() => setExpandedId(isExpanded ? null : n.id)}
                      className="-ml-1 flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                      aria-label={`${isExpanded ? 'Hide' : 'Show'} details for ${n.displayName}`}
                      aria-expanded={isExpanded}
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-3.5 w-3.5" strokeWidth={2} />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5" strokeWidth={2} />
                      )}
                    </button>
                  </td>
                  <td className="px-3 py-2 font-medium">
                    <span className="inline-flex items-center gap-2">
                      <Sparkles
                        className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                        strokeWidth={2}
                        aria-hidden
                      />
                      {n.displayName}
                      {n.isHidden && (
                        <Pill tone="muted" icon={EyeOff}>
                          Hidden
                        </Pill>
                      )}
                    </span>
                  </td>
                  <td className="px-3 py-2" data-label="Creator types">
                    {n.categoryCodes.length === 0 ? (
                      <Tag>All creator types</Tag>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {n.categoryCodes.map((c) => (
                          <Tag key={c}>{categoryName(c)}</Tag>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2" data-label="Focus">
                    {n.isFocused ? (
                      <Pill tone="pending">Focus</Pill>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right" data-label="Niche bonus">
                    {totalBonus > 0 ? (
                      <span className="font-semibold tabular-nums text-primary">
                        Up to +${formatMoney(totalBonus)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums" data-label="Invites">
                    {n.usage.invitesLive > 0 ? n.usage.invitesLive.toLocaleString() : (
                      <span className="text-muted-foreground">0</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right text-muted-foreground" data-label="Last used">
                    {formatDate(n.usage.lastUsedAt, 'Never')}
                  </td>
                  <td className="actions px-3 py-2 text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label={`Rename ${n.displayName}`}
                        title="Edit the name and creator types."
                        onClick={() => openEdit(n)}
                      >
                        <Pencil className="h-3.5 w-3.5" /> Rename
                      </Button>
                      <FocusToggle niche={n} />
                    </div>
                  </td>
                </DataTableRow>
                {isExpanded && (
                  <DataTableRow state="expanded">
                    <td className="hidden sm:table-cell"></td>
                    <DataTableExpandedCell colSpan={colCount - 1}>
                      <NicheDetailPanel
                        niche={n}
                        linkedRates={linkedRates}
                        categories={categories.data ?? []}
                        categoryName={categoryName}
                        onRemoved={() => setExpandedId(null)}
                      />
                    </DataTableExpandedCell>
                  </DataTableRow>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </DataTable>
    </div>
  );
}

function FocusToggle({ niche: n }: { niche: OutreachNicheWithUsage }): JSX.Element {
  const update = useUpdateNiche();
  return (
    <ConfirmDialog
      title={n.isFocused ? `Stop focusing ${n.displayName}?` : `Make ${n.displayName} a focus niche?`}
      description={
        <>
          {n.isFocused
            ? 'It will no longer be starred for outreachers or used to steer discovery searches.'
            : 'It will be starred for outreachers and used to steer discovery searches.'}{' '}
          Focus doesn&apos;t change niche bonuses or what any invite earns. To pay more for this
          niche, add a bonus from its details.
        </>
      }
      confirmLabel={n.isFocused ? 'Stop focusing' : 'Make focus niche'}
      pending={update.isPending}
      onConfirm={() =>
        update.mutateAsync({ id: n.id, body: { isFocused: !n.isFocused } }).then(
          () => {
            toast.success(
              n.isFocused
                ? `${n.displayName} is no longer a focus niche`
                : `${n.displayName} is now a focus niche`,
            );
          },
          (err: Error) => {
            toast.error(`Couldn't change focus for ${n.displayName}: ${err.message}`);
          },
        )
      }
      trigger={
        <Button
          size="sm"
          variant="ghost"
          aria-label={n.isFocused ? `Stop focusing ${n.displayName}` : `Make ${n.displayName} a focus niche`}
          title={n.isFocused ? 'Stop starring this niche for outreachers.' : 'Star this niche for outreachers.'}
        >
          {n.isFocused ? (
            <>
              <StarOff className="h-3.5 w-3.5" /> Unfocus
            </>
          ) : (
            <>
              <Star className="h-3.5 w-3.5" /> Focus
            </>
          )}
        </Button>
      }
    />
  );
}

function SectionLabel({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
      {children}
    </div>
  );
}

function NicheDetailPanel({
  niche,
  linkedRates,
  categories,
  categoryName,
  onRemoved,
}: {
  niche: OutreachNicheWithUsage;
  linkedRates: OutreachRate[];
  categories: OutreachCategory[];
  categoryName: (code: string) => string;
  onRemoved: () => void;
}): JSX.Element {
  const u = niche.usage;
  const usageLines: [string, number][] = [
    ['Live invites', u.invitesLive],
    ['All invites (incl. rejected / hidden)', u.invitesTotal],
    ['Prospects in queues', u.prospects],
    ['Creator requests', u.creatorRequests],
    ['Challenges targeting it', u.challenges],
    ['Active niche rates', u.ratesActive],
    ['Turned-off niche rates', u.ratesInactive],
    ['Discovery search suggestions', u.discoverySuggestions],
  ];

  return (
    // whitespace-normal: the table is nowrap, and unwrapped panel copy would
    // stretch every column to the panel's width.
    <div className="grid gap-6 whitespace-normal px-4 py-4 text-sm">
      <BonusGrid
        niche={niche}
        linkedRates={linkedRates}
        categories={categories}
        categoryName={categoryName}
      />

      <div className="grid gap-5 md:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <SectionLabel>Scope</SectionLabel>
          <div className="flex flex-wrap items-center gap-1.5 text-xs">
            {niche.categoryCodes.length === 0 ? (
              <span className="text-foreground/85">
                Every creator type: offered whatever creator type the outreacher logs.
              </span>
            ) : (
              <>
                <span className="text-muted-foreground">Only for</span>
                {niche.categoryCodes.map((c) => (
                  <Tag key={c}>{categoryName(c)}</Tag>
                ))}
              </>
            )}
          </div>
          <div className="mt-2">
            <SectionLabel>Focus and order</SectionLabel>
          </div>
          <div className="flex items-center gap-2 text-xs text-foreground/85">
            {niche.isFocused ? (
              <Pill tone="pending">Focus</Pill>
            ) : (
              <span className="text-muted-foreground">Not focused</span>
            )}
            <span className="text-muted-foreground">Position {niche.displayOrder} in lists</span>
            <span className="font-mono text-muted-foreground">ID {niche.code}</span>
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <SectionLabel>Where it&apos;s used</SectionLabel>
          <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-0.5 text-xs">
            {usageLines.map(([label, n]) => (
              <Fragment key={label}>
                <dt className="text-muted-foreground">{label}</dt>
                <dd className="text-right tabular-nums">{n.toLocaleString()}</dd>
              </Fragment>
            ))}
            <dt className="text-muted-foreground">Last used on an invite</dt>
            <dd className="text-right">{formatDate(u.lastUsedAt, 'Never')}</dd>
          </dl>
        </div>
      </div>

      <HideOrRemove niche={niche} activeBonusCount={linkedRates.length} onRemoved={onRemoved} />
    </div>
  );
}

// ── Bonuses ────────────────────────────────────────────────────────────────

type BonusEdit =
  | { mode: 'add'; category: string; rateType: BonusComponent }
  | { mode: 'change'; rate: OutreachRate };

/**
 * Creator type × component grid of this niche's bonuses. Every write goes
 * through the same rate endpoints (and hooks) as Compensation → Rates:
 * add = POST, change = PUT amount, remove = DELETE (turns the rate off; rows
 * are never deleted because payments may have been priced from them).
 */
function BonusGrid({
  niche,
  linkedRates,
  categories,
  categoryName,
}: {
  niche: OutreachNicheWithUsage;
  linkedRates: OutreachRate[];
  categories: OutreachCategory[];
  categoryName: (code: string) => string;
}): JSX.Element {
  const [edit, setEdit] = useState<BonusEdit | null>(null);
  const deactivate = useDeactivateRate();

  // Rows: creator types the niche is offered for (all active ones when it's
  // global), plus any type that already has a bonus row for it.
  const rowCodes = useMemo(() => {
    const offered = categories
      .filter((c) => c.isActive)
      .filter((c) => niche.categoryCodes.length === 0 || niche.categoryCodes.includes(c.code))
      .map((c) => c.code);
    const withRates = linkedRates.map((r) => r.category);
    return [...new Set([...offered, ...withRates])];
  }, [categories, niche.categoryCodes, linkedRates]);
  const formulaOf = (code: string): string | undefined =>
    categories.find((c) => c.code === code)?.payoutFormula;
  const otherRates = linkedRates.filter((r) => !isBonusComponent(r.rateType));
  const name = niche.displayName;

  const describe = (r: { category: string; rateType: string }): string =>
    `${categoryName(r.category)} · ${rateTypeLabel(r.rateType)}`;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <SectionLabel>Niche bonuses</SectionLabel>
        <span className="text-xs text-muted-foreground">
          Added on top of the base rate when {name} is the invite&apos;s first niche.
        </span>
      </div>
      {niche.isHidden && (
        <p className="flex items-start gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
          {name} is hidden, but its bonuses still apply to invites already tagged with it. Turn them
          off here to stop paying them. Unhide it to add new ones.
        </p>
      )}
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-xs" aria-label={`${name} bonuses`}>
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="px-3 py-2 font-semibold">Creator type</th>
              {BONUS_COMPONENTS.map((c) => (
                <th key={c} className="px-3 py-2 text-right font-semibold">
                  {rateTypeLabel(c)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rowCodes.length === 0 && (
              <tr className="border-t border-border">
                <td colSpan={4} className="px-3 py-3 text-muted-foreground">
                  No active creator types to pay a bonus for.
                </td>
              </tr>
            )}
            {rowCodes.map((cat) => {
              const flat = formulaOf(cat) === 'SIMPLE_LOG_BONUS';
              const outOfScope =
                niche.categoryCodes.length > 0 && !niche.categoryCodes.includes(cat);
              const canAdd = !niche.isHidden && !flat;
              return (
                <tr key={cat} className="border-t border-border">
                  <th scope="row" className="px-3 py-2 text-left font-medium">
                    {categoryName(cat)}
                    {flat && (
                      <span className="block text-[11px] font-normal text-muted-foreground">
                        Flat log rate: niche bonuses don&apos;t apply
                      </span>
                    )}
                    {outOfScope && (
                      <span className="block text-[11px] font-normal text-muted-foreground">
                        Niche not offered for this type
                      </span>
                    )}
                  </th>
                  {BONUS_COMPONENTS.map((comp) => {
                    const cell = linkedRates.filter((r) => r.category === cat && r.rateType === comp);
                    const label = `${categoryName(cat)} ${rateTypeLabel(comp).toLowerCase()} bonus for ${name}`;
                    return (
                      <td
                        key={comp}
                        className="px-3 py-2 text-right align-top"
                        // The outer table's mobile card-stack styles reach this
                        // nested table too; the label keeps each cell readable.
                        data-label={rateTypeLabel(comp)}
                      >
                        {cell.length === 0 ? (
                          canAdd ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 text-muted-foreground"
                              aria-label={`Add ${label}`}
                              onClick={() => setEdit({ mode: 'add', category: cat, rateType: comp })}
                            >
                              <Plus className="h-3 w-3" /> Add
                            </Button>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )
                        ) : (
                          <div className="flex flex-col items-end gap-1">
                            {cell.map((r) => (
                              <div key={r.id} className="flex items-center justify-end gap-1">
                                <span className="font-semibold tabular-nums text-primary">
                                  +${formatMoney(r.amount)}
                                </span>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-1.5"
                                  aria-label={`Change ${label}`}
                                  title={r.updatedAt ? `Last changed ${formatDate(r.updatedAt)}` : 'Change the amount'}
                                  onClick={() => setEdit({ mode: 'change', rate: r })}
                                >
                                  <Pencil className="h-3 w-3" />
                                </Button>
                                <ConfirmDialog
                                  title={`Remove the ${describe(r)} bonus for ${name}?`}
                                  tone="destructive"
                                  description={
                                    <>
                                      Turns off the ${formatMoney(r.amount)} rate. It&apos;s kept,
                                      not deleted, so past payments still trace to it.{' '}
                                      {repriceNote(name, 'they lose this bonus')}
                                    </>
                                  }
                                  confirmLabel="Remove bonus"
                                  pending={deactivate.isPending}
                                  onConfirm={() =>
                                    deactivate.mutateAsync(r.id).then(
                                      () => {
                                        toast.success(`${describe(r)} bonus for ${name} removed`);
                                      },
                                      (err: Error) => {
                                        toast.error(`Couldn't remove the bonus: ${err.message}`);
                                      },
                                    )
                                  }
                                  trigger={
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 px-1.5 text-destructive hover:text-destructive"
                                      aria-label={`Remove ${label}`}
                                    >
                                      <XCircle className="h-3 w-3" />
                                    </Button>
                                  }
                                />
                              </div>
                            ))}
                            {cell.length > 1 && (
                              <span className="text-[11px] text-warning">
                                {cell.length} active rows: only one is used. Remove the extra.
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {otherRates.length > 0 && (
        <div className="flex flex-col gap-1 text-xs">
          <span className="text-muted-foreground">
            Other rate rows for {name}. The payout calculator doesn&apos;t read these types for a
            niche, so they pay nothing:
          </span>
          {otherRates.map((r) => (
            <div key={r.id} className="flex items-center gap-2">
              <span>
                {describe(r)} · ${formatMoney(r.amount)}
              </span>
              <ConfirmDialog
                title={`Turn off ${describe(r)} for ${name}?`}
                tone="destructive"
                description="It doesn't pay anything today; turning it off just tidies the rate card. The row is kept, not deleted."
                confirmLabel="Turn off"
                pending={deactivate.isPending}
                onConfirm={() =>
                  deactivate.mutateAsync(r.id).then(
                    () => {
                      toast.success('Rate turned off');
                    },
                    (err: Error) => {
                      toast.error(`Couldn't turn off the rate: ${err.message}`);
                    },
                  )
                }
                trigger={
                  <Button size="sm" variant="ghost" className="h-7 text-destructive hover:text-destructive">
                    Turn off
                  </Button>
                }
              />
            </div>
          ))}
        </div>
      )}
      <p className="flex items-start gap-2 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
        {repriceNote(name)}
      </p>
      {edit && (
        <BonusDialog
          edit={edit}
          niche={niche}
          describe={describe}
          onClose={() => setEdit(null)}
        />
      )}
    </div>
  );
}

const AMOUNT_RE = /^\d+(\.\d{1,2})?$/;

function BonusDialog({
  edit,
  niche,
  describe,
  onClose,
}: {
  edit: BonusEdit;
  niche: OutreachNicheWithUsage;
  describe: (r: { category: string; rateType: string }) => string;
  onClose: () => void;
}): JSX.Element {
  const create = useCreateRate();
  const update = useUpdateRate();
  const target = edit.mode === 'add' ? edit : edit.rate;
  const [amount, setAmount] = useState(edit.mode === 'change' ? edit.rate.amount : '');
  const valid = AMOUNT_RE.test(amount.trim());
  const pending = create.isPending || update.isPending;
  const unchanged = edit.mode === 'change' && Number(amount) === Number(edit.rate.amount);
  const name = niche.displayName;

  async function save(): Promise<void> {
    if (!valid || pending || unchanged) return;
    const value = Number(amount.trim()).toFixed(2);
    try {
      if (edit.mode === 'add') {
        await create.mutateAsync({
          category: edit.category,
          rateType: edit.rateType,
          niche: niche.code,
          amount: value,
        });
        toast.success(`${describe(target)} bonus for ${name} added`);
      } else {
        await update.mutateAsync({ id: edit.rate.id, body: { amount: value } });
        toast.success(`${describe(target)} bonus for ${name} is now $${formatMoney(value)}`);
      }
      onClose();
    } catch (err) {
      toast.error(`Couldn't save the bonus: ${(err as Error).message}`);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {edit.mode === 'add' ? 'Add' : 'Change'} {describe(target)} bonus for {name}
          </DialogTitle>
          <DialogDescription>
            {edit.mode === 'change'
              ? `Currently +$${formatMoney(edit.rate.amount)}. `
              : 'Paid on top of the base rate. '}
            {repriceNote(name)}
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-1.5"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <Label htmlFor="bonusAmount">Bonus ($)</Label>
          <Input
            id="bonusAmount"
            inputMode="decimal"
            autoFocus
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="5.00"
            aria-invalid={amount !== '' && !valid}
          />
          {amount !== '' && !valid && (
            <p className="text-xs text-destructive">Enter an amount like 5 or 2.50.</p>
          )}
        </form>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!valid || pending || unchanged} onClick={() => void save()}>
            {edit.mode === 'add' ? 'Add bonus' : 'Save bonus'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Hide / remove ──────────────────────────────────────────────────────────

function HideOrRemove({
  niche,
  activeBonusCount,
  onRemoved,
}: {
  niche: OutreachNicheWithUsage;
  activeBonusCount: number;
  onRemoved: () => void;
}): JSX.Element {
  const update = useUpdateNiche();
  const remove = useDeleteNiche();
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const references = nicheReferences(niche.usage);
  const name = niche.displayName;

  const setHidden = (isHidden: boolean): Promise<void> =>
    update.mutateAsync({ id: niche.id, body: { isHidden } }).then(
      () => {
        toast.success(
          isHidden
            ? `${name} is hidden. Turn on "Show hidden" to find it again.`
            : `${name} is back in the pickers`,
        );
      },
      (err: Error) => {
        toast.error(`Couldn't ${isHidden ? 'hide' : 'unhide'} ${name}: ${err.message}`);
      },
    );

  return (
    <div className="flex flex-col gap-2 border-t border-border pt-4">
      <SectionLabel>Hide or remove</SectionLabel>
      <div className="flex flex-wrap items-start gap-3">
        {niche.isHidden ? (
          <ConfirmDialog
            title={`Unhide ${name}?`}
            description="It goes back into the log form, niche pickers, bulk import, rate creation, challenge targets and discovery's focus list."
            confirmLabel="Unhide"
            pending={update.isPending}
            onConfirm={() => setHidden(false)}
            trigger={
              <Button size="sm" variant="outline" aria-label={`Unhide ${name}`}>
                <Eye className="h-3.5 w-3.5" /> Unhide
              </Button>
            }
          />
        ) : (
          <ConfirmDialog
            title={`Hide ${name}?`}
            description={
              <>
                Outreachers and admins can&apos;t pick it any more: it leaves the log form, niche
                pickers, bulk import mapping, rate creation, challenge targets
                {niche.isFocused ? " and discovery's focus list" : ''}. Invites, prospects,
                reports and payroll that already use it keep showing it.{' '}
                {activeBonusCount > 0
                  ? `Its ${activeBonusCount} bonus ${activeBonusCount === 1 ? 'rate keeps' : 'rates keep'} applying to invites already tagged with it until you remove ${activeBonusCount === 1 ? 'it' : 'them'} above.`
                  : 'It has no active bonuses.'}{' '}
                You can unhide it any time.
              </>
            }
            confirmLabel="Hide niche"
            pending={update.isPending}
            onConfirm={() => setHidden(true)}
            trigger={
              <Button size="sm" variant="outline" aria-label={`Hide ${name}`}>
                <EyeOff className="h-3.5 w-3.5" /> Hide
              </Button>
            }
          />
        )}
        {references.length === 0 ? (
          <Button
            size="sm"
            variant="outline"
            className="text-destructive hover:text-destructive"
            aria-label={`Remove ${name}`}
            onClick={() => setConfirmingRemove(true)}
          >
            <Trash2 className="h-3.5 w-3.5" /> Remove
          </Button>
        ) : (
          <p className="max-w-[60ch] text-xs text-muted-foreground">
            Can&apos;t remove {name}: it&apos;s used by {references.join(', ')}. Removing it would
            leave those pointing at a niche that no longer exists. Hide it instead.
          </p>
        )}
      </div>
      {confirmingRemove && (
        <RemoveNicheDialog
          niche={niche}
          pending={remove.isPending}
          onCancel={() => setConfirmingRemove(false)}
          onConfirm={() =>
            remove.mutateAsync(niche.id).then(
              () => {
                toast.success(`${name} removed`);
                setConfirmingRemove(false);
                onRemoved();
              },
              (err: Error) => {
                toast.error(`Couldn't remove ${name}: ${err.message}`);
              },
            )
          }
        />
      )}
    </div>
  );
}

/** Typed-name confirmation: removing a niche can't be undone. */
function RemoveNicheDialog({
  niche,
  pending,
  onCancel,
  onConfirm,
}: {
  niche: OutreachNicheWithUsage;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}): JSX.Element {
  const [typed, setTyped] = useState('');
  const matches = typed.trim() === niche.displayName;
  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Remove {niche.displayName}?</DialogTitle>
          <DialogDescription>
            Nothing uses this niche, so it can be deleted for good. This can&apos;t be undone; add it
            again later if you need it.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-1.5">
          <Label htmlFor="confirmNicheName">
            Type <span className="font-semibold">{niche.displayName}</span> to confirm
          </Label>
          <Input
            id="confirmNicheName"
            autoFocus
            autoComplete="off"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="destructive" disabled={!matches || pending} onClick={onConfirm}>
            Remove niche
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface CategoryChipsProps {
  categories: Array<{ code: string; displayName: string; isActive: boolean }>;
  selected: string[];
  onToggle: (code: string) => void;
}

function CategoryChips({ categories, selected, onToggle }: CategoryChipsProps): JSX.Element {
  return (
    <div className="grid gap-1">
      <Label>Creator types</Label>
      <div className="flex flex-wrap gap-2">
        {categories.map((c) => {
          const active = selected.includes(c.code);
          return (
            <button
              key={c.code}
              type="button"
              onClick={() => onToggle(c.code)}
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                active
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-input bg-transparent text-foreground hover:bg-muted'
              }`}
              aria-pressed={active}
            >
              {c.displayName}
            </button>
          );
        })}
        {categories.length === 0 && (
          <p className="text-xs text-muted-foreground">No active creator types yet.</p>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Leave all off to offer this niche for every creator type. Pick one or more to offer it
        only for those.
      </p>
    </div>
  );
}
